import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import React, { useCallback, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { isAfterOrEqualDates, isBeforeOrEqualDates } from "../helpers/dates";
import { DEFAULT_WEEKDAYS } from "../index";
import { ValueTypes } from "../shared/types";
import { ApplyTo } from "./applyTo";
import { DateRange } from "./dateRange";
import { messages } from "./messages";
import { ButtonsContainer, CancelButton, StyledLoader, UpdateButton } from "./styles";
import { UpdateValue } from "./updateValue";

export enum AriType {
  INVENTORY_LINES = "INVENTORY",
  EXTRAS = "EXTRA",
  PROMOS = "DISCOUNT",
  COMPLEMENTS = "COMPLEMENT",
  BUNDLES = "BUNDLE",
}

export enum AriFilterType {
  ACCOMMODATION = "accommodation",
  CONDITION = "condition",
  MEAL_PLAN = "mealPlan",
  CONFIGURATION = "configuration",
}

interface Props {
  items?: string[];
  periods?: {
    startDate: Date;
    endDate: Date;
    weekdays: number[];
  }[];
  valueType?: ValueTypes;
  onSubmit: (
    valueType: ValueTypes,
    operator: string,
    value: number | boolean,
    periods: {
      startDate: Date;
      endDate: Date;
      weekdays: number[];
    }[],
    inventories: string[],
    type: AriType
  ) => void;
  onClose: () => void;
  showWeekDays?: boolean;
  showAddPeriod?: boolean;
  showApplyTo?: boolean;
  inventoryLines?: {
    ticker: string;
    name: string;
    accommodation?: string;
    condition?: string;
    mealPlan?: string;
    occupancy?: string;
    valueTypes: ValueTypes[];
  }[];
  complements?: {
    ticker: string;
    valueTypes: ValueTypes[];
  }[];
  extras?: {
    ticker: string;
    valueTypes: ValueTypes[];
  }[];
  promos?: {
    ticker: string;
    valueTypes: ValueTypes[];
  }[];
  bundles?: {
    ticker: string;
    name: string;
    accommodation?: string;
    condition?: string;
    mealPlan?: string;
    occupancy?: string;
    valueTypes: ValueTypes[];
  }[];
  accommodations: { value: string; label: string }[];
  conditions: { value: string; label: string }[];
  mealPlans: { value: string; label: string }[];
  configurations: { value: string; label: string }[];
  type?: AriType;
  updating: boolean;
  isSpecificDate?: boolean;
}

const getInitialValue = (valueType: ValueTypes | undefined): boolean | undefined => {
  if (!valueType) {
    return;
  }

  switch (valueType) {
    case ValueTypes.closed:
    case ValueTypes.closeToArrival:
    case ValueTypes.closeToDeparture:
      return false;

    default:
      return;
  }
};

export const UpdateForm = ({
  items,
  periods,
  valueType,
  onSubmit,
  onClose,
  showWeekDays,
  showAddPeriod,
  showApplyTo,
  inventoryLines,
  complements,
  extras,
  promos,
  bundles,
  accommodations,
  conditions,
  mealPlans,
  configurations,
  type,
  updating,
  isSpecificDate,
}: Props): JSX.Element => {
  const [selectedValueType, setSelectedValueType] = useState<ValueTypes>(
    valueType || ValueTypes.rate
  );
  const [operator, setOperator] = useState<string>("=");
  const [value, setValue] = useState<number | boolean | undefined>(getInitialValue(valueType));
  const [selectedPeriods, setPeriods] = useState(periods || []);
  const [applyTo, setApplyTo] = useState<AriType>(AriType.INVENTORY_LINES);
  const [selectedItems, setSelectedItems] = useState<string[]>(items || []);
  const [accommodationFilter, setAccommodationFilter] = useState<string[]>([]);
  const [conditionFilter, setConditionFilter] = useState<string[]>([]);
  const [mealPlanFilter, setMealPlanFilter] = useState<string[]>([]);
  const [occupancyFilter, setOccupancyFilter] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const defaultValue = useMemo(() => getInitialValue(selectedValueType), [selectedValueType]);
  const selectedType = useMemo(() => type ?? applyTo, [type, applyTo]);

  const getValueTypes = useCallback(
    (applyTo: AriType) => {
      const sourceMap: Record<AriType, { valueTypes: ValueTypes[] }[] | undefined> = {
        [AriType.INVENTORY_LINES]: inventoryLines,
        [AriType.EXTRAS]: extras,
        [AriType.COMPLEMENTS]: complements,
        [AriType.BUNDLES]: bundles,
        [AriType.PROMOS]: promos,
      };

      const source = sourceMap[applyTo];
      if (source) {
        const types = new Set<ValueTypes>(source.flatMap(b => b.valueTypes));
        return [...types];
      }

      return [];
    },
    [bundles, complements, extras, inventoryLines, promos]
  );
  const valueTypes = useMemo(() => getValueTypes(applyTo), [getValueTypes, applyTo]);

  const filteredInventories = useMemo(() => {
    if (!inventoryLines) {
      return;
    }

    let finalInventoryLines = [...inventoryLines];
    if (accommodationFilter && accommodationFilter.length) {
      finalInventoryLines = finalInventoryLines.filter(
        it => it.accommodation && accommodationFilter.includes(it.accommodation)
      );
    }
    if (conditionFilter && conditionFilter.length) {
      finalInventoryLines = finalInventoryLines.filter(
        it => it.condition && conditionFilter.includes(it.condition)
      );
    }
    if (mealPlanFilter && mealPlanFilter.length) {
      finalInventoryLines = finalInventoryLines.filter(
        it => it.mealPlan && mealPlanFilter.includes(it.mealPlan)
      );
    }
    if (occupancyFilter && occupancyFilter.length) {
      finalInventoryLines = finalInventoryLines.filter(
        it => it.occupancy && occupancyFilter.includes(it.occupancy)
      );
    }
    return finalInventoryLines;
  }, [accommodationFilter, conditionFilter, inventoryLines, mealPlanFilter, occupancyFilter]);

  const filteredBundles = useMemo(() => {
    let finalBundles = bundles ? [...bundles] : [];
    if (accommodationFilter && accommodationFilter.length) {
      finalBundles = finalBundles.filter(
        it => it.accommodation && accommodationFilter.includes(it.accommodation)
      );
    }
    if (conditionFilter && conditionFilter.length) {
      finalBundles = finalBundles.filter(
        it => it.condition && conditionFilter.includes(it.condition)
      );
    }
    if (mealPlanFilter && mealPlanFilter.length) {
      finalBundles = finalBundles.filter(it => it.mealPlan && mealPlanFilter.includes(it.mealPlan));
    }
    if (occupancyFilter && occupancyFilter.length) {
      finalBundles = finalBundles.filter(
        it => it.occupancy && occupancyFilter.includes(it.occupancy)
      );
    }
    return finalBundles;
  }, [accommodationFilter, bundles, conditionFilter, mealPlanFilter, occupancyFilter]);

  const handleChangeFromDate = useCallback(
    (date: Date, idx: number) => {
      let result = [...selectedPeriods];
      result[idx] = {
        startDate: date,
        endDate:
          result[idx].endDate && isBeforeOrEqualDates(date, result[idx].endDate)
            ? result[idx].endDate
            : date,
        weekdays: result[idx].weekdays || DEFAULT_WEEKDAYS,
      };
      setPeriods(result);
    },
    [selectedPeriods]
  );

  const handleChangeToDate = useCallback(
    (date: Date, idx: number) => {
      let result = [...selectedPeriods];
      result[idx] = {
        startDate:
          result[idx].startDate && isAfterOrEqualDates(date, result[idx].startDate)
            ? result[idx].startDate
            : date,
        endDate: date,
        weekdays: result[idx].weekdays || DEFAULT_WEEKDAYS,
      };
      setPeriods(result);
    },
    [selectedPeriods]
  );

  const handleChangeWeekdays = useCallback(
    (weekday: number, idx: number) => {
      let result = [...selectedPeriods];
      const weekdays = result[idx].weekdays;
      if (weekdays.includes(weekday)) {
        weekdays.splice(weekdays.indexOf(weekday), 1);
      }
      result[idx].weekdays = weekdays;
      setPeriods(result);
    },
    [selectedPeriods]
  );

  const handleChangeFilter = useCallback(
    (filter: AriFilterType, value: string) => {
      switch (filter) {
        case AriFilterType.ACCOMMODATION:
          let accommodationFilterCopy = [...accommodationFilter];
          if (accommodationFilterCopy.includes(value)) {
            accommodationFilterCopy.splice(accommodationFilterCopy.indexOf(value), 1);
            setAccommodationFilter(accommodationFilterCopy);
            return;
          }
          accommodationFilterCopy.push(value);
          setAccommodationFilter(accommodationFilterCopy);
          break;

        case AriFilterType.CONDITION:
          let conditionFilterCopy = [...conditionFilter];
          if (conditionFilterCopy.includes(value)) {
            conditionFilterCopy.splice(conditionFilterCopy.indexOf(value), 1);
            setConditionFilter(conditionFilterCopy);
            return;
          }
          conditionFilterCopy.push(value);
          setConditionFilter(conditionFilterCopy);
          break;

        case AriFilterType.MEAL_PLAN:
          let mealPlanFilterCopy = [...mealPlanFilter];
          if (mealPlanFilterCopy.includes(value)) {
            mealPlanFilterCopy.splice(mealPlanFilterCopy.indexOf(value), 1);
            setMealPlanFilter(mealPlanFilterCopy);
            return;
          }
          mealPlanFilterCopy.push(value);
          setMealPlanFilter(mealPlanFilterCopy);
          break;

        case AriFilterType.CONFIGURATION:
          let occupancyFilterCopy = [...occupancyFilter];
          if (occupancyFilterCopy.includes(value)) {
            occupancyFilterCopy.splice(occupancyFilterCopy.indexOf(value), 1);
            setOccupancyFilter(occupancyFilterCopy);
            return;
          }
          occupancyFilterCopy.push(value);
          setOccupancyFilter(occupancyFilterCopy);
          break;

        default:
          break;
      }
    },
    [accommodationFilter, conditionFilter, mealPlanFilter, occupancyFilter]
  );

  const handleChangeSelectedItems = useCallback(
    (ticker: string, selected: boolean) => {
      if (selected) {
        setSelectedItems([...selectedItems, ticker]);
      }
      if (!selected) {
        const result = [...selectedItems];
        result.splice(
          result.findIndex(it => it === ticker),
          1
        );
        setSelectedItems(result);
      }
    },
    [selectedItems]
  );

  const handleSelectAll = useCallback(() => {
    switch (applyTo) {
      case AriType.INVENTORY_LINES:
        if (!filteredInventories) {
          return;
        }
        setSelectedItems(filteredInventories.map(it => it.ticker));
        return;

      case AriType.BUNDLES:
        if (!filteredBundles) {
          return;
        }
        setSelectedItems(filteredBundles.map(it => it.ticker));
        return;
    }
  }, [applyTo, filteredInventories, filteredBundles]);

  const handleUnselectAll = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const handleSubmit = useCallback(() => {
    const finalValue = value === undefined ? defaultValue : value;
    if (
      finalValue === undefined ||
      !operator ||
      !selectedPeriods ||
      !selectedPeriods.length ||
      !selectedItems ||
      !selectedItems.length
    ) {
      if (value === undefined && defaultValue === undefined) {
        setFormErrors(["value"]);
      }
      return;
    }

    onSubmit(
      selectedValueType || ValueTypes.rate,
      operator,
      finalValue,
      selectedPeriods,
      selectedItems,
      selectedType
    );
  }, [
    selectedValueType,
    operator,
    value,
    defaultValue,
    selectedPeriods,
    selectedItems,
    selectedType,
    onSubmit,
  ]);

  const canApplyTo = useMemo(() => Boolean(showApplyTo && filteredInventories), [
    showApplyTo,
    filteredInventories,
  ]);

  const handleAddPeriod = useCallback(() => {
    if (selectedPeriods) {
      setPeriods([
        ...selectedPeriods,
        { startDate: new Date(), endDate: new Date(), weekdays: DEFAULT_WEEKDAYS },
      ]);
    }
  }, [selectedPeriods]);

  const handleRemovePeriod = useCallback(
    (idx: number) => {
      if (selectedPeriods) {
        const result = [...selectedPeriods];
        result.splice(idx, 1);
        setPeriods(result);
      }
    },
    [selectedPeriods]
  );

  const handleSelectAllFilters = useCallback(
    (filter: AriFilterType) => {
      switch (filter) {
        case AriFilterType.ACCOMMODATION:
          setAccommodationFilter(accommodations.map(it => it.value));
          break;

        case AriFilterType.CONDITION:
          setConditionFilter(conditions.map(it => it.value));
          break;

        case AriFilterType.MEAL_PLAN:
          setMealPlanFilter(mealPlans.map(it => it.value));
          break;

        case AriFilterType.CONFIGURATION:
          setOccupancyFilter(configurations.map(it => it.value));
          break;
      }
    },
    [accommodations, conditions, mealPlans, configurations]
  );

  const handleUnselectAllFilters = useCallback((filter: AriFilterType) => {
    switch (filter) {
      case AriFilterType.ACCOMMODATION:
        setAccommodationFilter([]);
        break;

      case AriFilterType.CONDITION:
        setConditionFilter([]);
        break;

      case AriFilterType.MEAL_PLAN:
        setMealPlanFilter([]);
        break;

      case AriFilterType.CONFIGURATION:
        setOccupancyFilter([]);
        break;
    }
  }, []);

  const handleApplyTo = useCallback(
    (value: AriType) => {
      const valueTypes = getValueTypes(value);
      setSelectedValueType(valueTypes.includes(ValueTypes.rate) ? ValueTypes.rate : valueTypes[0]);
      setApplyTo(value);
    },
    [getValueTypes]
  );

  return (
    <>
      <UpdateValue
        valueType={selectedValueType}
        valueTypes={valueTypes}
        canChangeValueType={!valueType}
        onChangeValueType={setSelectedValueType}
        onChangeOperator={setOperator}
        onChangeValue={setValue}
        errors={formErrors}
        isBlockingDay={isSpecificDate}
      />

      <DateRange
        periods={selectedPeriods}
        onChangeFromDate={handleChangeFromDate}
        onChangeToDate={handleChangeToDate}
        onChangeWeekday={handleChangeWeekdays}
        onAddPeriod={handleAddPeriod}
        onRemovePeriod={handleRemovePeriod}
        showWeekDays={showWeekDays}
        showAddPeriod={showAddPeriod}
        disabled={isSpecificDate}
        showOneDate={isSpecificDate}
      />

      {canApplyTo && (
        <ApplyTo
          applyTo={applyTo}
          handleApplyTo={handleApplyTo}
          handleChangeFilter={handleChangeFilter}
          handleOnChange={handleChangeSelectedItems}
          handleSelectAll={handleSelectAll}
          handleUnselectAll={handleUnselectAll}
          handleSelectAllFilters={handleSelectAllFilters}
          handleUnselectAllFilters={handleUnselectAllFilters}
          inventoryLines={
            filteredInventories
              ? filteredInventories.filter(it => it.valueTypes.includes(selectedValueType))
              : []
          }
          complements={
            complements && complements.length
              ? complements.filter(it => it.valueTypes.includes(selectedValueType))
              : undefined
          }
          extras={
            extras && extras.length
              ? extras.filter(it => it.valueTypes.includes(selectedValueType))
              : undefined
          }
          promos={
            promos && promos.length
              ? promos.filter(it => it.valueTypes.includes(selectedValueType))
              : undefined
          }
          bundles={
            filteredBundles && filteredBundles.length
              ? filteredBundles.filter(it => it.valueTypes.includes(selectedValueType))
              : undefined
          }
          accommodations={accommodations}
          conditions={conditions}
          mealPlans={mealPlans}
          configurations={configurations}
          selectedItems={selectedItems}
          selectedFilters={{
            accommodations: accommodationFilter,
            conditions: conditionFilter,
            mealPlans: mealPlanFilter,
            occupancies: occupancyFilter,
          }}
        />
      )}

      <ButtonsContainer>
        <UpdateButton onClick={handleSubmit} disabled={updating}>
          {!updating && <FormattedMessage {...messages.update} />}
          {updating && (
            <>
              <FormattedMessage {...messages.updating} />
              <StyledLoader icon={faSyncAlt} spin />
            </>
          )}
        </UpdateButton>
        <CancelButton onClick={onClose} disabled={updating}>
          <FormattedMessage {...messages.cancel} />
        </CancelButton>
      </ButtonsContainer>
    </>
  );
};
